// TargetingForm.js
// Client: vmfive
// Version: 1.0.0
// Standalone, reusable targeting configuration form.
// Extracted from CreateAdWizard Step 3 for reuse in edit flows.

import React from 'react';
import { TargetingSection, KeywordsDialog, Pill } from '../ds';
import DayPartsGrid from '../DayPartsGrid/DayPartsGrid';
import AudienceSelector from '../AudienceSelector/AudienceSelector';
import './TargetingForm.css';

// ─── Static options ───

const COUNTRY_OPTIONS = [
  { value: 'TW', label: '台灣 (TW)' },
  { value: 'JP', label: '日本 (JP)' },
  { value: 'HK', label: '香港 (HK)' },
  { value: 'MY', label: '馬來西亞 (MY)' },
  { value: 'SG', label: '新加坡 (SG)' },
  { value: 'US', label: '美國 (US)' },
  { value: 'KR', label: '韓國 (KR)' },
  { value: 'TH', label: '泰國 (TH)' },
  { value: 'VN', label: '越南 (VN)' },
  { value: 'PH', label: '菲律賓 (PH)' },
  { value: 'ID', label: '印尼 (ID)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'zh', label: '中文 (zh)' },
  { value: 'en', label: 'English (en)' },
  { value: 'ja', label: '日本語 (ja)' },
  { value: 'ko', label: '한국어 (ko)' },
  { value: 'th', label: 'ไทย (th)' },
  { value: 'vi', label: 'Tiếng Việt (vi)' },
  { value: 'ms', label: 'Bahasa Melayu (ms)' },
  { value: 'id', label: 'Bahasa Indonesia (id)' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: '男' },
  { value: 'female', label: '女' },
  { value: 'unknown', label: '不明' },
];

const AGE_OPTIONS = [
  { value: '0017', label: '0-17' },
  { value: '1824', label: '18-24' },
  { value: '2534', label: '25-34' },
  { value: '3544', label: '35-44' },
  { value: '4554', label: '45-54' },
  { value: '5564', label: '55-64' },
  { value: '6574', label: '65-74' },
  { value: '7599', label: '75+' },
  { value: 'unknown', label: '不明' },
];

// ─── Icons (inline SVG, 20px, stroke 2px, currentColor) ───

const icons = {
  freq: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="8" />
      <path d="M10 5v5l3 3" />
    </svg>
  ),
  keywords: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 3v14M3 7h8M3 13h8M13 5l4 5-4 5" />
    </svg>
  ),
  region: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="8" />
      <path d="M2 10h16M10 2a12 12 0 010 16M10 2a12 12 0 000 16" />
    </svg>
  ),
  demo: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="7" cy="7" r="3" />
      <circle cx="14" cy="7" r="3" />
      <path d="M1 17a6 6 0 0112 0M8 17a6 6 0 0112 0" />
    </svg>
  ),
  dayParts: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="16" height="14" rx="2" />
      <path d="M2 8h16M7 3v5M13 3v5" />
    </svg>
  ),
  audience: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="2" />
      <circle cx="10" cy="10" r="5" />
      <circle cx="10" cy="10" r="8" />
    </svg>
  ),
};

// ─── Helpers ───

const toggleInArray = (arr, val) =>
  arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

const labelList = (selected, options, max = 3) => {
  if (!selected.length) return null;
  const labels = selected.map((v) => {
    const opt = options.find((o) => o.value === v);
    return opt ? opt.label : v;
  });
  if (labels.length <= max) return labels.join('、');
  return `${labels.slice(0, max).join('、')} 等 ${labels.length} 項`;
};

// ─── Component ───

export default function TargetingForm({
  // Feature flags
  enabledFeatures,
  // Frequency capping
  freqEnabled, onFreqEnabledChange,
  freqTotal, onFreqTotalChange,
  freqDaily, onFreqDailyChange,
  // Keywords
  keywordsEnabled, onKeywordsEnabledChange,
  includeKeywords, onIncludeKeywordsChange,
  excludeKeywords, onExcludeKeywordsChange,
  // Region & Language
  regionEnabled, onRegionEnabledChange,
  countryWhitelist, onCountryWhitelistChange,
  languageWhitelist, onLanguageWhitelistChange,
  // Demographic
  demoEnabled, onDemoEnabledChange,
  genderWhitelist, onGenderWhitelistChange,
  ageWhitelist, onAgeWhitelistChange,
  // Day Parts
  dayPartsActive, onDayPartsActiveChange,
  dayPartsValue, onDayPartsValueChange,
  // Audience
  audienceEnabled, onAudienceEnabledChange,
  audienceWhitelist, onAudienceWhitelistChange,
  audienceBlacklist, onAudienceBlacklistChange,
}) {
  const isFeatureEnabled = (key) => {
    if (!enabledFeatures) return false;
    return enabledFeatures[key] !== false;
  };

  const hasAnyFeature = enabledFeatures && (
    isFeatureEnabled('frequency_cap') ||
    isFeatureEnabled('day_parts') ||
    isFeatureEnabled('keywords') ||
    isFeatureEnabled('country') ||
    isFeatureEnabled('language') ||
    isFeatureEnabled('demographic') ||
    isFeatureEnabled('audience')
  );

  // ─── Summary builders ───

  const freqSummary = freqEnabled
    ? [freqTotal && `總上限 ${freqTotal}`, freqDaily && `日上限 ${freqDaily}`].filter(Boolean).join('、') || null
    : null;

  const keywordsSummary = keywordsEnabled
    ? [
        includeKeywords.length > 0 && `包含 ${includeKeywords.length} 個`,
        excludeKeywords.length > 0 && `排除 ${excludeKeywords.length} 個`,
      ].filter(Boolean).join('、') || null
    : null;

  const regionSummary = regionEnabled
    ? [
        labelList(countryWhitelist, COUNTRY_OPTIONS),
        labelList(languageWhitelist, LANGUAGE_OPTIONS),
      ].filter(Boolean).join(' / ') || null
    : null;

  const demoSummary = demoEnabled
    ? [
        labelList(genderWhitelist, GENDER_OPTIONS, 3),
        labelList(ageWhitelist, AGE_OPTIONS, 3),
      ].filter(Boolean).join(' / ') || null
    : null;

  const dayPartsSummary = dayPartsActive ? '已設定時段' : null;

  const audienceSummary = audienceEnabled
    ? [
        audienceWhitelist.length > 0 && `投放 ${audienceWhitelist.length} 個`,
        audienceBlacklist.length > 0 && `排除 ${audienceBlacklist.length} 個`,
      ].filter(Boolean).join('、') || null
    : null;

  return (
    <div className="targeting-form">
      <div className="targeting-form__header">
        <h2>Targeting 設定</h2>
        <p className="targeting-form__description">
          選擇要啟用的 targeting 維度，未啟用的項目將不限制
        </p>
      </div>

      {!hasAnyFeature && (
        <p className="targeting-form__empty">此帳號沒有可用的 Targeting 選項</p>
      )}

      {/* Frequency Capping */}
      {isFeatureEnabled('frequency_cap') && (
        <TargetingSection
          title="頻次控制"
          icon={icons.freq}
          summary={freqSummary}
          enabled={freqEnabled}
          onToggle={() => onFreqEnabledChange(!freqEnabled)}
        >
          <div className="form-row">
            <div className="form-group">
              <label>每人總曝光上限</label>
              <input type="number" min="0" max="999" value={freqTotal}
                onChange={(e) => onFreqTotalChange(e.target.value)} placeholder="不限制" />
            </div>
            <div className="form-group">
              <label>每人每日曝光上限</label>
              <input type="number" min="0" max="999" value={freqDaily}
                onChange={(e) => onFreqDailyChange(e.target.value)} placeholder="不限制" />
            </div>
          </div>
        </TargetingSection>
      )}

      {/* Keywords */}
      {isFeatureEnabled('keywords') && (
        <TargetingSection
          title="關鍵字"
          icon={icons.keywords}
          summary={keywordsSummary}
          enabled={keywordsEnabled}
          onToggle={() => onKeywordsEnabledChange(!keywordsEnabled)}
        >
          <KeywordsDialog
            label="包含關鍵字"
            value={includeKeywords}
            onChange={onIncludeKeywordsChange}
            placeholder="每行輸入一個關鍵字，例如：購物"
          />
          <KeywordsDialog
            label="排除關鍵字"
            value={excludeKeywords}
            onChange={onExcludeKeywordsChange}
            placeholder="每行輸入一個排除關鍵字"
          />
        </TargetingSection>
      )}

      {/* Region & Language */}
      {(isFeatureEnabled('country') || isFeatureEnabled('language')) && (
        <TargetingSection
          title="地區與語言"
          icon={icons.region}
          summary={regionSummary}
          enabled={regionEnabled}
          onToggle={() => onRegionEnabledChange(!regionEnabled)}
        >
          {isFeatureEnabled('country') && (
            <div className="form-group">
              <label>國家/地區</label>
              <div className="pill-options">
                {COUNTRY_OPTIONS.map((opt) => (
                  <Pill key={opt.value}
                    active={countryWhitelist.includes(opt.value)}
                    onClick={() => onCountryWhitelistChange(toggleInArray(countryWhitelist, opt.value))}>
                    {opt.label}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {isFeatureEnabled('language') && (
            <div className="form-group">
              <label>語言</label>
              <div className="pill-options">
                {LANGUAGE_OPTIONS.map((opt) => (
                  <Pill key={opt.value}
                    active={languageWhitelist.includes(opt.value)}
                    onClick={() => onLanguageWhitelistChange(toggleInArray(languageWhitelist, opt.value))}>
                    {opt.label}
                  </Pill>
                ))}
              </div>
            </div>
          )}
        </TargetingSection>
      )}

      {/* Demographic */}
      {isFeatureEnabled('demographic') && (
        <TargetingSection
          title="人口統計"
          icon={icons.demo}
          summary={demoSummary}
          enabled={demoEnabled}
          onToggle={() => onDemoEnabledChange(!demoEnabled)}
        >
          <div className="form-group">
            <label>性別</label>
            <div className="pill-options">
              {GENDER_OPTIONS.map((opt) => (
                <Pill key={opt.value}
                  active={genderWhitelist.includes(opt.value)}
                  onClick={() => onGenderWhitelistChange(toggleInArray(genderWhitelist, opt.value))}>
                  {opt.label}
                </Pill>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>年齡</label>
            <div className="pill-options">
              {AGE_OPTIONS.map((opt) => (
                <Pill key={opt.value}
                  active={ageWhitelist.includes(opt.value)}
                  onClick={() => onAgeWhitelistChange(toggleInArray(ageWhitelist, opt.value))}>
                  {opt.label}
                </Pill>
              ))}
            </div>
          </div>
        </TargetingSection>
      )}

      {/* Day Parts */}
      {isFeatureEnabled('day_parts') && (
        <TargetingSection
          title="時段鎖定"
          icon={icons.dayParts}
          summary={dayPartsSummary}
          enabled={dayPartsActive}
          onToggle={() => {
            const next = !dayPartsActive;
            onDayPartsActiveChange(next);
            if (next) {
              const isEmpty = Object.values(dayPartsValue).every(arr => arr.length === 0);
              if (isEmpty) {
                const ALL_SLOTS = [];
                for (let h = 0; h < 24; h++) {
                  const hh = String(h).padStart(2, '0');
                  ALL_SLOTS.push(`${hh}00`, `${hh}30`);
                }
                const all = {};
                ['mon','tue','wed','thu','fri','sat','sun'].forEach(k => { all[k] = [...ALL_SLOTS]; });
                onDayPartsValueChange(all);
              }
            }
          }}
        >
          <DayPartsGrid
            value={dayPartsValue}
            onChange={onDayPartsValueChange}
            isActive={dayPartsActive}
            onActiveChange={onDayPartsActiveChange}
          />
        </TargetingSection>
      )}

      {/* Audience */}
      {isFeatureEnabled('audience') && (
        <TargetingSection
          title="受眾標籤"
          icon={icons.audience}
          summary={audienceSummary}
          enabled={audienceEnabled}
          onToggle={() => onAudienceEnabledChange(!audienceEnabled)}
        >
          <AudienceSelector label="投放受眾" value={audienceWhitelist} onChange={onAudienceWhitelistChange} />
          <div style={{ height: 20, borderBottom: '1px solid var(--border-color)', marginBottom: 20 }} />
          <AudienceSelector label="排除受眾" value={audienceBlacklist} onChange={onAudienceBlacklistChange} />
        </TargetingSection>
      )}
    </div>
  );
}
